import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { HistoryField } from 'components/Field/HistoryField';
import { ImageField } from 'componentsAware/ImageField';
import { ReviewStatusField } from 'components/Field/ReviewStatusField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import {
  ModalScreenProps,
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import {
  filterArrayEmpty,
  randomKey,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual, pick, sortBy } from 'lodash';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  eventAseSelectFindingQuestion,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/app/actions';
import {
  doAseCreateControl,
  doAseDeleteControl,
  doAseOpenFindingLocationModal,
  doAseUpdateControl,
} from 'store/assessments/actions';
import {
  selectAseCategories,
  selectAseControlStatuses,
  selectAseAssetId,
  selectAseTplSections,
  selectAseTplQuestions,
  selectAseResultQuestion,
  selectAseIsOnline,
  selectAseId,
} from 'store/assessments/selectors';
import { returnDefaultControl } from 'store/assessments/templates';
import { AssessmentItemTypes, Control } from 'store/assessments/types';
import { DueDeterminedBy, FilesContextEnum } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_CONTROL
>;

export const ControlEdit: React.FC<
  | ModalScreenProps<ModalScreens.AseControl>
  | {
      route: Pick<Props['route'], 'params'>;
      navigation: {
        goBack: Props['navigation']['goBack'];
        setOptions: Props['navigation']['setOptions'];
      };
      isModal?: undefined;
    }
> = ({ route, navigation, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirmUnsaved = useConfirmUnsaved();
  const confirm = useConfirm();
  const forceUpdate = useForceUpdate();
  const { id, isDuplicate, parent, data: incomingData } = route.params;

  const isOnline = useSelector(selectAseIsOnline);
  const assessmentId = useSelector(selectAseId);

  const eventKeySelectCategory = React.useRef(randomKey());
  const fieldKeyLocation = React.useRef(0);
  const eventKeySelectLocation = React.useRef(randomKey());

  const dataRef = React.useRef<Control>(cloneDeep(incomingData));
  const internalSave = !!isModal;

  const isNew = !id;
  const control: Control = dataRef.current;

  const assetId = useSelector(selectAseAssetId);
  const isAttachedToFieldInstance = !!control.fieldInstanceId;
  const controlStatuses = useSelector(selectAseControlStatuses);
  const categories = useSelector(selectAseCategories);
  const templateQuestions = useSelector(selectAseTplQuestions);
  const templateSections = useSelector(selectAseTplSections);

  const answerId = useSelector((state) =>
    selectAseResultQuestion(
      state,
      control.questionId || 'na',
      control.fieldInstanceId,
    ),
  )?.answerId;

  const category = categories[control.categoryId] || categories['0']!;

  const isDirtyRef = React.useRef(false);
  const checkIsDirty = React.useCallback(
    () => !isEqual(incomingData, dataRef.current),
    [incomingData],
  );
  isDirtyRef.current = checkIsDirty();

  const handleHardSave = React.useCallback(() => {
    if (!id) {
      dispatch(doAseCreateControl({ data: dataRef.current }));
    } else if (checkIsDirty()) {
      dispatch(doAseUpdateControl({ id, data: dataRef.current }));
    } else if (isModal) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [checkIsDirty, dispatch, id, isModal, navigation, screenSize]);

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleHardSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleHardSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const handleSoftSave = React.useCallback(
    (
      changes: Partial<Control>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = { ...dataRef.current, ...changes };
        if (
          doForceUpdate === 'always' ||
          (internalSave &&
            doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        }
        if (!internalSave) {
          dispatch(doAseUpdateControl({ id, data: newValues }));
        }
      }
    },
    [checkIsDirty, dispatch, forceUpdate, id, internalSave],
  );

  const saveField = React.useCallback(
    (name: keyof Control, value: string) =>
      handleSoftSave({ [name]: value }, { doForceUpdate: 'dirtyChange' }),
    [handleSoftSave],
  );

  const saveReviewStatus = React.useCallback(
    (reviewStatusId: string) =>
      handleSoftSave({ reviewStatusId }, { doForceUpdate: 'always' }),
    [handleSoftSave],
  );

  const handleImageChange = React.useCallback(
    (value: string[]) =>
      handleSoftSave(
        {
          photoIds: value,
        },
        { doForceUpdate: 'always' },
      ),
    [handleSoftSave],
  );

  const handleDelete = () =>
    confirm({
      title: control.isReview ? 'Delete Review' : 'Delete Control',
      text: control.isReview
        ? 'Are you sure you want to delete the review for this control?'
        : 'Are you sure you want to delete this control?',
      confirmText: 'Delete',
      onConfirm: () => dispatch(doAseDeleteControl({ id: control.id })),
      destructive: true,
    });

  const handleCategoryPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Category',
          eventKey: eventKeySelectCategory.current,
          value: control.categoryId ? [control.categoryId] : undefined,
          options: sortBy(
            Object.values(categories).map((cat) => ({
              name: cat.name,
              groupName: cat.group_name,
              internalValue: cat.id,
              ordering:
                (cat.group_name === 'Uncategorised' ? 0 : cat.group_name) +
                cat.name,
            })),
            ['ordering'],
          ),
        },
      }),
    );

  const handleSuggestTemplate = () =>
    !control.questionId
      ? dispatch(
          ALERT({
            text:
              'Controls cannot be suggested for sections. ' +
              'Move this control to a question or checklist before turning it into a suggestion',
          }),
        )
      : dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseMakeSuggestion,
            params: {
              type: 'control',
              description: control.description,
              why: control.why,
              categoryId: category.id,
              severityId: '0',
              timescaleId: '0',
              dueDeterminedBy: DueDeterminedBy.timescaleBased,
              dueTimescale: null,
              dueTypeId: '',
              questionId: control.questionId,
              answerId: answerId || undefined,
              cost: '',
            },
          }),
        );

  const handleDuplicatePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseControl,
        params: {
          id: '',
          isDuplicate: true,
          parent,
          data: {
            ...returnDefaultControl({
              questionId: control.questionId,
              sectionId: control.sectionId,
              fieldInstanceId: control.fieldInstanceId,
              suggestionId: control.suggestionId,
            }),
            ...pick(control, ['description', 'why', 'where', 'categoryId']),
          },
        },
      }),
    );

  const selectQuestionEventKey = React.useRef(randomKey()).current;
  const handleMovePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASEFINDINGBELONGSTO,
        params: {
          findingType: AssessmentItemTypes.CONTROL,
          questionId: control.questionId,
          sectionId: control.sectionId,
          fieldInstanceId: control.fieldInstanceId,
          eventKey: selectQuestionEventKey,
        },
      }),
    );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventAseSelectFindingQuestion, (event) => {
        if (event.payload.key === selectQuestionEventKey) {
          handleSoftSave(event.payload.data, { doForceUpdate: 'always' });
        }
      }),
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeySelectCategory.current) {
          handleSoftSave(
            {
              categoryId: event.payload.stringValues[0],
            },
            { doForceUpdate: 'always' },
          );
        } else if (event.payload.key === eventKeySelectLocation.current) {
          fieldKeyLocation.current += 1;
          handleSoftSave(
            {
              where: event.payload.stringValues[0] || '',
            },
            { doForceUpdate: 'always' },
          );
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [handleSoftSave, selectQuestionEventKey]);

  const isDirtyVar = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(isModal && handleClose);

  return (
    <PageScrollView
      isInModal={!!isModal}
      header={
        <NavigationHeader
          onPressSaveButton={isModal ? handleHardSave : undefined}
          onPressCancelButton={isModal ? handleClose : undefined}
          onPressBackButton={
            !isModal && screenSize === 'small' ? navigation?.goBack : undefined
          }
          heading={`${isDuplicate ? 'Duplicate ' : isNew ? 'New ' : ''}Control`}
          menuRight={
            isNew
              ? undefined
              : {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    !isAttachedToFieldInstance
                      ? {
                          text: 'Move',
                          icon: ICONS.HandLight,
                          onPress: handleMovePress,
                        }
                      : null,
                    {
                      text: 'Duplicate',
                      icon: ICONS.CloneLight,
                      onPress: handleDuplicatePress,
                    },
                    isOnline
                      ? null
                      : {
                          text: 'Suggest',
                          icon: ICONS.LightbulbOnLight,
                          onPress: handleSuggestTemplate,
                        },
                    '-',
                    {
                      text: control.isReview
                        ? 'Delete Review'
                        : 'Delete Control',
                      icon: ICONS.TrashCanLight,
                      destructive: true,
                      onPress: handleDelete,
                    },
                  ].filter(filterArrayEmpty),
                }
          }
        />
      }
    >
      {!isNew && (
        <Card>
          <ViewWithSeparators horizontal style={{ flex: 1 }}>
            <CardRow
              icon={ICONS.RBPlectrumSolid}
              label={
                control.reference
                  ? rbAddReferencePrefix(control.reference)
                  : undefined
              }
              style={{ flex: 1 }}
            >
              {!control.reference && (
                <Txt>
                  {rbAddReferencePrefix('')}
                  <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
                </Txt>
              )}
            </CardRow>
            <HistoryField
              history={control.history}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.ASEHISTORY,
                    params: {
                      id,
                      findingType: AssessmentItemTypes.CONTROL,
                    },
                  }),
                )
              }
            />
          </ViewWithSeparators>
        </Card>
      )}

      {!!control.reviewStatusId && (
        <Card>
          <ReviewStatusField
            options={controlStatuses.map((status) => ({
              label: status.name,
              value: status.id,
            }))}
            value={control.reviewStatusId}
            onChange={saveReviewStatus}
          />
          <TextAreaField
            placeholder="Review Note"
            value={control.reviewNote}
            name="reviewNote"
            onChangeWithName={saveField}
            onEsc={handleClose}
          />
        </Card>
      )}

      <Card margin={false}>
        <TextAreaField
          placeholder="What is the control? (required)"
          value={control.description}
          name="description"
          onChangeWithName={saveField}
          onEsc={handleClose}
          autoFocusWeb={isModal}
          autoFocusDelay
        />
        <TextAreaField
          placeholder="Why is it needed?"
          value={control.why}
          name="why"
          onChangeWithName={saveField}
          onEsc={handleClose}
        />
        <TextField
          placeholder="Location"
          key={`location-${fieldKeyLocation.current}`}
          value={control.where}
          name="where"
          onChangeWithName={saveField}
          showTextIcon
          onEsc={handleClose}
        />
      </Card>

      <View
        style={{
          marginBottom: theme.paddingVertical,
          paddingHorizontal: theme.paddingHorizontal,
        }}
      >
        <Button
          uppercase={false}
          text="Location Suggestions"
          buttonTheme={ButtonTheme.link}
          onPress={() =>
            dispatch(
              doAseOpenFindingLocationModal({
                eventKey: eventKeySelectLocation.current,
                value: control.where,
              }),
            )
          }
          textWeight="regular"
          style={{ marginTop: 6, alignSelf: 'flex-start' }}
        />
      </View>

      {Object.values(categories).length > 1 && (
        <Card>
          <CardRow
            label="Category"
            value={category.name}
            onPress={handleCategoryPress}
            icon={ICONS.TagLight}
          />
        </Card>
      )}

      <Card>
        <ImageField
          context={
            isOnline
              ? {
                  context: FilesContextEnum.remoteAssessment,
                  assetId,
                  assessmentId,
                }
              : {
                  context: FilesContextEnum.localAssessment,
                  assetId,
                  assessmentId,
                }
          }
          value={control.photoIds}
          onChange={handleImageChange}
          promptMessage="Add to Control"
          multiSelect
        />
      </Card>

      <Card>
        <CardRow
          label="Belongs To"
          labelAlignTop
          value={
            control.questionId !== null
              ? templateQuestions[control.questionId]?.question
              : control.sectionId
                ? templateSections[control.sectionId]?.name
                : ''
          }
          onPress={handleMovePress}
        />
      </Card>
    </PageScrollView>
  );
};
